import analytics from '../../services/analytics';

export const INIT_VIDEO_STREAM_SUCCESS = 'INIT_VIDEO_STREAM_SUCCESS';
export const INIT_SCREEN_STREAM_SUCCESS = 'INIT_SCREEN_STREAM_SUCCESS';
export const STOP_VIDEO_STREAM = 'STOP_VIDEO_STREAM';
export const STOP_SCREEN_STREAM = 'STOP_SCREEN_STREAM';
export const SET_DEVICE_CONTROLLER = 'SET_DEVICE_CONTROLLER';
export const SET_MEETING_SESSION = 'SET_MEETING_SESSION';
export const STREAM_ERROR = 'STREAM_ERROR';
export const END_MEETING_SESSION = 'END_MEETING_SESSION';
export const INIT_DEVICES_SUCCESS = 'INIT_DEVICES_SUCCESS';
export const SET_SELECTED_DEVICE = 'SET_SELECTED_DEVICE';
export const OPEN_MEDIA_RECOVERY_MODAL = 'OPEN_MEDIA_RECOVERY_MODAL';
export const CLOSE_MEDIA_RECOVERY_MODAL = 'CLOSE_MEDIA_RECOVERY_MODAL';
export const SET_SCREEN_SHARE_INITIATED = 'SET_SCREEN_SHARE_INITIATED';
export const CLEAR_SCREEN_SHARE_INITIATED = 'CLEAR_SCREEN_SHARE_INITIATED';
export const SET_MEDIA_SHARE_COMPLETED = 'SET_MEDIA_SHARE_COMPLETED';

export const initDevices = deviceController => async dispatch => {
  try {
    const videoInputs = await deviceController.listVideoInputDevices();
    const audioInputs = await deviceController.listAudioInputDevices();
    const audioOutputs = await deviceController.listAudioOutputDevices();

    dispatch({
      type: INIT_DEVICES_SUCCESS,
      payload: { videoInputs, audioInputs, audioOutputs },
    });

    dispatch({
      type: SET_DEVICE_CONTROLLER,
      payload: deviceController,
    });

    return deviceController;
  } catch (error) {
    dispatch({
      type: STREAM_ERROR,
      payload: 'Failed to initialize devices',
    });
    throw error;
  }
};

export const setSelectedDevice = (kind, deviceId) => async (dispatch, getState) => {
  const { meetingSession } = getState().assesmentRecording;
  try {
    if (kind === 'video') {
      await meetingSession.audioVideo.startVideoInputDevice(deviceId);
    } else if (kind === 'audio') {
      await meetingSession.audioVideo.startAudioInput(deviceId);
    } else if (kind === 'speaker') {
      await meetingSession.audioVideo.chooseAudioOutput(deviceId);
    }

    dispatch({
      type: SET_SELECTED_DEVICE,
      payload: { kind, deviceId },
    });
  } catch {
    dispatch({
      type: STREAM_ERROR,
      payload: `Failed to set ${kind} device`,
    });
  }
};

export const initVideoStream = () => async dispatch => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    analytics.trackCameraPermissionGranted();
    analytics.trackMicrophonePermissionGranted();
    dispatch({
      type: INIT_VIDEO_STREAM_SUCCESS,
      payload: stream,
    });
    return stream;
  } catch (error) {
    analytics.trackCameraPermissionDenied();
    analytics.trackMicrophonePermissionDenied();
    dispatch({
      type: STREAM_ERROR,
      payload: 'Failed to initialize video stream',
    });
    throw error;
  }
};

const handleStop = dispatch => {
  dispatch(stopScreenStream());
};

export const initScreenStream = () => async (dispatch, getState) => {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor' },
      audio: true,
    });

    analytics.trackScreenShareStarted();

    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();

    if (settings.displaySurface && settings.displaySurface !== 'monitor') {
      stream.getTracks().forEach(track => track.stop());
      throw new Error('You are required to share your entire screen to continue the exam.');
    }

    let screenShareEndedDispatched = false;

    const handleScreenShareEnd = () => {
      if (!screenShareEndedDispatched) {
        screenShareEndedDispatched = true;
        console.log('Screen share stopped (track ended)');
        analytics.trackScreenShareStopped();
        dispatch(stopScreenStream());

        const assessmentSubmissionStatus = getState().assessment.assessmentSubmissionStatus;
        if (assessmentSubmissionStatus === '') {
          dispatch(openMediaRecoveryModal('screenshare-stopped'));
        }
      }
    };

    const [videoTrack] = stream.getVideoTracks();
    videoTrack.onended = handleScreenShareEnd;

    stream.getTracks().forEach(track => {
      track.onended = handleScreenShareEnd;
    });

    dispatch({
      type: INIT_SCREEN_STREAM_SUCCESS,
      payload: stream,
    });
    return stream;
  } catch (error) {
    analytics.trackScreenShareFailed(error.message || 'unknown_error');
    dispatch({
      type: STREAM_ERROR,
      payload: error,
    });
    throw error;
  }
};

export const stopVideoStream = () => (dispatch, getState) => {
  console.log('Stopping video stream');
  const { videoStream } = getState().assesmentRecording;
  if (videoStream) {
    videoStream.getTracks().forEach(track => track.stop());
  }
  dispatch({ type: STOP_VIDEO_STREAM });
};

export const stopScreenStream = () => (dispatch, getState) => {
  console.log('Stopping screen stream');
  const { screenStream } = getState().assesmentRecording;
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
  }
  dispatch({ type: STOP_SCREEN_STREAM });
};

export const setDeviceController = controller => ({
  type: SET_DEVICE_CONTROLLER,
  payload: controller,
});

export const endMeetingSession = meetingSession => async dispatch => {
  if (meetingSession) {
    const { audioVideo } = meetingSession;

    if (audioVideo.stopVideoInput) {
      await audioVideo.stopVideoInput();
    }

    if (audioVideo.stopAudioInput) {
      await audioVideo.stopAudioInput();
    }

    try {
      await audioVideo.stopContentShare();
    } catch (err) {
      console.warn('[Chime] Content share cleanup skipped:', err);
    }

    audioVideo.stop();

    if (audioVideo.realtimeUnsubscribeFromAllEvents) {
      audioVideo.realtimeUnsubscribeFromAllEvents();
    }
  }
  dispatch(stopAllStreams());
  dispatch({ type: END_MEETING_SESSION });
};

export const setMeetingSession = session => ({
  type: SET_MEETING_SESSION,
  payload: session,
});

export const startMeetingSession = session => dispatch => {
  // Add observer BEFORE starting the meeting session
  const videoTileObserver = {
    videoTileDidUpdate: tileState => {
      console.log('Video tile updated:', tileState);
      dispatch(videoTileDidUpdate(tileState));
    },
    videoTileWasRemoved: tileId => {
      console.log('Video tile removed:', tileId);
      dispatch(videoTileWasRemoved(tileId));
    },
  };

  session.audioVideo.addObserver(videoTileObserver);

  // Start the meeting session
  session.audioVideo.start();
  session.audioVideo.startLocalVideoTile();

  dispatch({
    type: SET_MEETING_SESSION,
    payload: session,
  });
};

export const stopAllStreams = () => (dispatch, getState) => {
  const { videoStream, audioStream, screenStream } = getState().assesmentRecording;

  const stopTracks = stream => {
    if (stream) {
      stream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (err) {
          console.warn('Failed to stop track:', err);
        }
      });
    }
  };

  stopTracks(videoStream);
  stopTracks(audioStream);
  stopTracks(screenStream);

  dispatch({ type: STOP_VIDEO_STREAM });
  dispatch({ type: STOP_SCREEN_STREAM });

  console.log('All the streams stops or not');
};

export const openMediaRecoveryModal = reason => ({
  type: OPEN_MEDIA_RECOVERY_MODAL,
  payload: reason || 'unknown',
});

export const closeMediaRecoveryModal = () => ({
  type: CLOSE_MEDIA_RECOVERY_MODAL,
});

export const setHasVideo = hasVideo => ({
  type: 'SET_HAS_VIDEO',
  payload: hasVideo,
});

export const setScreenShareInitiated = () => ({
  type: SET_SCREEN_SHARE_INITIATED,
  payload: Date.now(),
});

export const clearScreenShareInitiated = () => ({
  type: CLEAR_SCREEN_SHARE_INITIATED,
});

export const setMediaShareCompleted = () => ({
  type: SET_MEDIA_SHARE_COMPLETED,
});

export const VIDEO_TILE_DID_UPDATE = 'VIDEO_TILE_DID_UPDATE';
export const VIDEO_TILE_WAS_REMOVED = 'VIDEO_TILE_WAS_REMOVED';

export const videoTileDidUpdate = tileState => ({
  type: VIDEO_TILE_DID_UPDATE,
  payload: { tileState },
});

export const videoTileWasRemoved = tileId => ({
  type: VIDEO_TILE_WAS_REMOVED,
  payload: { tileId },
});
